/**
 * Content area for the Schedule Interview wizard.
 * Renders mode-driven step tabs/stepper with panels.
 */
import { useMemo } from 'react';
import { Flex, Text } from '@chakra-ui/react';
import { Tabs, type TabItem } from '@kula-ai/components';
import type { ScheduleModeConfig, ScheduleStepProps } from '../config/types';
import type { CurrentUserZoomStatus } from '../initialValues/useWizardInitialValues';
import { DetailsStep } from '../steps/details/DetailsStep';
import { CommunicationStep } from '../steps/communication/CommunicationStep';
import { ScorecardStep } from '../steps/scorecard/ScorecardStep';
import { SlotSelectionEmailStep } from '../steps/slotSelectionEmail/SlotSelectionEmailStep';

const STEP_COMPONENTS: Record<string, React.ComponentType<any>> = {
  details: DetailsStep,
  communication: CommunicationStep,
  scorecard: ScorecardStep,
  slotSelectionEmail: SlotSelectionEmailStep,
};

const PANEL_BASE =
  '!mt-0 flex min-h-0 flex-1 flex-col overflow-hidden ' +
  'data-[state=inactive]:!flex-none data-[state=inactive]:!p-0 data-[state=inactive]:!mt-0';

// Stepper mode hides the tab-list bottom border (`!border-b-0`), so the panel
// supplies its own top divider. Line/tabs mode already gets a bottom border
// from the tab list — adding one here doubles it up.
const PANEL_BORDER = 'border-t border-secondary-100';

interface ScheduleContentProps {
  config: ScheduleModeConfig;
  stepProps: ScheduleStepProps;
  interviewPlan?: Record<string, unknown>;
  /** Adhoc-only: current user's Zoom status (paints the Host chip when
   *  `interviewPlan` is intentionally undefined for adhoc). */
  currentUserZoomStatus?: CurrentUserZoomStatus;
  /** Adhoc-only: plan-level zoom host strategy. Drives auto-pick order. */
  planZoomHostType?: 'organizer' | 'interviewer' | string | null;
  candidateName?: string;
  candidateAvatar?: string;
  jobTitle?: string;
  getStepRef: (_stepKey: string) => React.RefObject<any>;
  activeStep: string;
  onStepChange: (_value: string) => void;
}

export const ScheduleContent = ({
  config,
  stepProps,
  interviewPlan,
  currentUserZoomStatus,
  planZoomHostType,
  candidateName,
  candidateAvatar,
  jobTitle,
  getStepRef,
  activeStep,
  onStepChange,
}: ScheduleContentProps) => {
  const tabItems = useMemo(
    (): TabItem[] =>
      config.steps.map((step) => {
        const StepComponent = STEP_COMPONENTS[step.key];
        const stepRef = getStepRef(step.key);
        return {
          label: step.label,
          value: step.key,
          panel: StepComponent ? (
            <StepComponent
              ref={stepRef}
              {...stepProps}
              {...(step.key === 'details'
                ? {
                    interviewPlan,
                    currentUserZoomStatus,
                    planZoomHostType,
                    candidateName,
                    candidateAvatar,
                  }
                : step.key === 'scorecard'
                  ? { interviewPlan }
                  : step.key === 'communication'
                    ? { interviewPlan, candidateName, jobTitle }
                    : step.key === 'slotSelectionEmail'
                      ? { candidateName, jobTitle }
                      : {})}
            />
          ) : (
            <Text>Step under developement: {step.key}</Text>
          ),
        };
      }),
    [
      config.steps,
      stepProps,
      getStepRef,
      interviewPlan,
      currentUserZoomStatus,
      planZoomHostType,
      candidateName,
      candidateAvatar,
      jobTitle,
    ]
  );

  const isStepper = config.navigationStyle !== 'tabs';

  return (
    <Flex flex="1" flexDirection="column" minH={0}>
      <Tabs.Root
        variant={isStepper ? 'stepper' : 'line'}
        items={tabItems}
        value={activeStep}
        onValueChange={onStepChange}
        preservePanels
        className="flex h-full min-h-0 w-full flex-1 flex-col"
        panelClassName={`${PANEL_BASE} ${isStepper ? PANEL_BORDER : ''} ${
          activeStep === 'details'
            ? 'data-[state=active]:!p-0'
            : 'data-[state=active]:p-4'
        }`}
        tabListWrapperClassName={
          isStepper
            ? // Stepper cap sizes up with the step count — 600px
              // fits the 3-step manual / adhoc configs, but the
              // 4-step self-schedule variants ("Details / Slot
              // selection email / Communication / Scorecard")
              // truncate labels at that width. Give 4+ steps a
              // roomier cap so each label stays fully readable.
              `shrink-0 ${config.steps.length >= 4 ? 'max-w-[760px]' : 'max-w-[600px]'} !border-b-0 px-4 pt-1 pb-3`
            : // Line variant: pad the tab list so "Details /
              // Communication / Scorecard" visually aligns with the
              // surrounding drawer chrome (header + panel content use
              // the same `px-6` gutter). Without this the tabs sit
              // flush against the drawer edge while everything else
              // has padding.
              'shrink-0 px-6'
        }
      />
    </Flex>
  );
};
